import React, { useState } from 'react';
import styled from 'styled-components';
import {
  FlexColumn,
  Button,
  ButtonProps,
} from '@elementary/common-ui/components';
import {
  SectionTitle,
  SectionDescription,
} from '@elementary/common-ui/components/FormControls';
import { ReactComponent as PlusIcon } from '@elementary/common-ui/assets/plus-2.svg';
import { useFormContext, useFieldArray } from 'react-hook-form';
import { WarehouseType } from '@elementary/common-ui/types/types';
import { AssetSourceFormData, EnvironmentForm } from 'shared/types';
import { radius, space } from '@elementary/common-ui/style/utils';
import AssetSourceAccordion from './AssetSourceAccordion';
import { getDefaultDbtAssetSource, switchAssetSourceKind } from './utils';

type Props = {
  warehouseType: WarehouseType;
  existingAssetSourceIds?: string[];
  disabled?: boolean;
};

const Wrapper = styled(FlexColumn)`
  gap: ${space('xxl')};
`;

const SourcesContainer = styled.div`
  border-radius: ${radius('md')};
  overflow: hidden;
`;

const AddSourceButton = styled(Button)<ButtonProps>`
  && {
    width: 100%;
    border: 1px solid ${({ theme }) => theme.palette.border.default};
    border-top: none;
    border-radius: ${radius(0, 0, 'lg', 'lg')};
    padding: ${space('lg')};
    background-color: ${({ theme }) => theme.palette.surface.base};
    color: ${({ theme }) => theme.palette.content.primary};
    height: 52px;

    &:hover:not(:disabled) {
      background-color: ${({ theme }) => theme.palette.surface.emphasis};
    }

    &:disabled {
      color: ${({ theme }) => theme.palette.content.disabled};
    }
  }
`;

const EnvironmentContentStep = ({
  warehouseType,
  existingAssetSourceIds = [],
  disabled,
}: Props) => {
  const { control, watch } = useFormContext<EnvironmentForm>();
  const [expandedIndex, setExpandedIndex] = useState<number | null>(0);

  const { fields, append, remove, update } = useFieldArray({
    control,
    name: 'assetSources',
  });

  const assetSources = watch('assetSources');

  const handleAddSource = () => {
    append(getDefaultDbtAssetSource(warehouseType));
    setExpandedIndex(fields.length);
  };

  const handleToggleAccordion = (index: number) => {
    setExpandedIndex(expandedIndex === index ? null : index);
  };

  const handleKindChange = (
    index: number,
    kind: AssetSourceFormData['type'],
  ) => {
    const current = assetSources[index];
    if (!current) {
      return;
    }
    update(index, switchAssetSourceKind(current, kind, warehouseType));
  };

  const isExistingSource = (index: number) => {
    const source = assetSources[index];
    return source?.id != null && existingAssetSourceIds.includes(source.id);
  };

  return (
    <Wrapper>
      <FlexColumn gap={1.5}>
        <SectionTitle>Asset sources</SectionTitle>
        <SectionDescription>
          Choose what Elementary monitors in this environment — dbt projects,
          warehouse schemas, or both.
        </SectionDescription>
      </FlexColumn>

      <SourcesContainer>
        {fields.map((field, index) => {
          const source = assetSources[index];
          if (!source) {
            return null;
          }
          return (
            <AssetSourceAccordion
              key={field.id}
              index={index}
              source={source}
              warehouseType={warehouseType}
              isExisting={isExistingSource(index)}
              onRemove={() => remove(index)}
              onKindChange={(kind) => handleKindChange(index, kind)}
              canRemove={fields.length > 1}
              disabled={disabled}
              isFirst={index === 0}
              expanded={expandedIndex === index}
              onToggle={() => handleToggleAccordion(index)}
            />
          );
        })}
        <AddSourceButton
          variant="text"
          startIcon={<PlusIcon />}
          onClick={handleAddSource}
          disabled={disabled}
        >
          Add asset source
        </AddSourceButton>
      </SourcesContainer>
    </Wrapper>
  );
};

export default EnvironmentContentStep;
